import * as React from 'react'
import styled, { css } from 'styled-components'

import * as behaviours from '../../../behaviours'
import * as S from '../../../styles'
import * as colors from '../../../constants/colors'

import { FieldStatus } from './FieldStatus'
import { RadioField } from './RadioField'
import { Field } from './Field'
import { radioGroupContext } from './RadioGroupContext'

export type RadioGroupFieldProps = {
  className?: string
  /** @default 'big' */
  type?: 'small' | 'medium' | 'big'
  error?: React.ReactNode
  children?: React.ReactNode
  label?: React.ReactNode
  name?: string
}

/** usually used with `<RadioField>`. */
export const RadioGroupField = styled(RadioGroupFieldComponent)<RadioGroupFieldProps>``
RadioGroupField.displayName = 'RadioGroupField'

function RadioGroupFieldComponent({
  className,
  type = 'big',
  error,
  children,
  label,
  name = String(Math.random()),
}: RadioGroupFieldProps): React.ReactElement<RadioGroupFieldProps> {
  const { classList } = behaviours.getStatusClassName(className, error)

  if (type === 'big') {
    return (
      <S.radio.BigGroup className={classList.filter(Boolean).join(' ')}>
        <S.field.TitleWrapper>
          <S.field.Title>{label}</S.field.Title>
        </S.field.TitleWrapper>
        <BigRadioList $isError={Boolean(error)}>
          <radioGroupContext.Provider value={{ name, type }}>{children}</radioGroupContext.Provider>
        </BigRadioList>
        <FieldStatus error={error} />
      </S.radio.BigGroup>
    )
  } else if (type === 'medium') {
    return (
      <S.radio.MediumGroup className={classList.filter(Boolean).join(' ')}>
        <MediumGroupList $isError={error !== undefined}>
          <radioGroupContext.Provider value={{ name, type }}>{children}</radioGroupContext.Provider>
        </MediumGroupList>
        <FieldStatus error={error} />
      </S.radio.MediumGroup>
    )
  } else {
    return (
      <S.radio.SmallGroup className={classList.filter(Boolean).join(' ')}>
        <S.field.TitleWrapper>
          <S.field.Title>{label}</S.field.Title>
        </S.field.TitleWrapper>
        <SmallRadioList $isError={Boolean(error)}>
          <radioGroupContext.Provider value={{ name, type }}>{children}</radioGroupContext.Provider>
        </SmallRadioList>
        <FieldStatus error={error} />
      </S.radio.SmallGroup>
    )
  }
}

export const SmallRadioList = styled.div<{ $isError: boolean }>`
  display: inline-flex;
  flex-direction: row;

  ${({ $isError }) =>
    $isError &&
    css`
      border: 1px dashed ${colors.PRIMARY_RED};
    `}
  ${Field} {
    border: none !important;
    padding: 9px 9px 9px 25px;
  }

  ${S.field.Description} {
    display: none;
  }

  ${S.field.Title} {
    font-size: 13px;
    line-height: 18px;
  }

  ${S.input.HiddenFocusableInput}:disabled ~ ${Field} {
    background-color: ${colors.PRIMARY_WHITE};
  }
`

export const BigRadioList = styled.div<{ $isError: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 1px;
  background-color: ${colors.PRIMARY_WHITE};

  ${S.field.FieldHead} {
    && {
      margin-bottom: 0;
    }
  }

  ${S.field.FieldHead}:has(+ ${S.field.Description}) {
    margin-bottom: 2px;

    label {
      display: block;
    }
  }

  ${RadioField} ${Field} {
    box-sizing: border-box;
    background-color: transparent;
    margin-top: -1px;
    min-height: 50px;
  }

  ${S.field.Title} {
    font-weight: 600;
    margin-right: 0;
  }

  ${S.field.TitleWrapper} {
    flex: 0;
  }

  ${S.field.Description} {
    color: ${colors.TEXT_TERTIARY};
    flex: 0;
  }
`

export const MediumGroupList = styled.div<{ $isError: boolean }>`
  background-color: ${colors.PRIMARY_WHITE};
  display: flex;
  flex-direction: row;

  ${RadioField} ${Field} {
    background-color: transparent;
    box-sizing: border-box;
    height: 50px;
    margin: 0 0 0 8px;
  }

  ${S.field.TitleWrapper} {
    flex: 0;
  }

  ${S.field.Description} {
    flex: 0;
  }
`
